/**
 * Media query for mobile detection (consistent with CSS)
 */
const _gridBannerMobileMediaQuery = window.matchMedia('(max-width: 749px)');

/**
 * Get columns count based on current screen width
 * @param {HTMLElement} productGrid - The product grid element
 * @returns {number} Number of columns
 */
function gridBannerGetColumnsCount(productGrid) {
  if (!productGrid) return 4;
  return _gridBannerMobileMediaQuery.matches
    ? (parseInt(productGrid.dataset.columnsMobile) || 2)
    : (parseInt(productGrid.dataset.columnsDesktop) || 4);
}

/**
 * Check if banners in last row should be hidden
 * Hides banners if they are alone in an incomplete last row (no products)
 * @param {HTMLElement} productGrid - The product grid element
 */
function gridBannerCheckBannersInLastRow(productGrid) {
  if (!productGrid) return;

  const banners = productGrid.querySelectorAll('.grid__item--banner');
  if (banners.length === 0) return;

  // Reset all banners visibility first
  banners.forEach(banner => banner.classList.remove('grid__item--banner-hidden'));

  const allItems = Array.from(productGrid.querySelectorAll('.grid__item'));
  if (allItems.length === 0) return;

  const columns = gridBannerGetColumnsCount(productGrid);

  // Find items in the last row by comparing offsetTop
  const lastItem = allItems[allItems.length - 1];
  const lastRowTop = lastItem.offsetTop;
  const lastRowItems = allItems.filter(item => item.offsetTop === lastRowTop);

  // Check if last row is incomplete
  if (lastRowItems.length < columns) {
    const bannersInLastRow = lastRowItems.filter(item => item.classList.contains('grid__item--banner'));
    const productsInLastRow = lastRowItems.filter(item => !item.classList.contains('grid__item--banner'));

    // Hide banners if only banners are in last row (no products)
    if (bannersInLastRow.length > 0 && productsInLastRow.length === 0) {
      bannersInLastRow.forEach(banner => banner.classList.add('grid__item--banner-hidden'));
    }
  }
}

class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();

    // blog or collection - !important
    this.sectionType = this.dataset.type;

    this.nameWrapper = this.sectionType === 'collection' ? '#product-grid' : '.blog-articles';
    this.nameWrapperChildren = this.sectionType === 'collection' ? '.grid__item' : '.article';

    if (this.dataset?.searchPage === 'true') {
      this.nameWrapper = '#product-grid > ul.product-grid';
    }

    this.productsGrid = document.querySelector(this.nameWrapper);
    this.paginationNextUrl = this.dataset.nextUrl;
    this.button = this.querySelector('[data-collection-more="button"]');
    this.spinner = this.querySelector('[data-collection-more="loading"]');
    this.perPage = parseInt(this.dataset.loadPerpage);
    this.targetPerPage = parseInt(this.dataset.targetPerPage) || this.perPage;
    this.queue = []; // Buffer for extra products

    this.checkVisibility();
    this.onLoadMoreProducts = this.handleLoadMoreProducts.bind(this);
  }

  connectedCallback() {
    this.button.addEventListener('click', this.onLoadMoreProducts);

    // Initial check for collection/search products
    if (this.productsGrid) {
      // Initialize ratings for already existing products on page load
      this.initWidgets();
      
      if (this.sectionType === 'collection') {
        requestAnimationFrame(() => gridBannerCheckBannersInLastRow(this.productsGrid));
      }
    }
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.onLoadMoreProducts);
  }

  checkVisibility() {
    // If we have items in queue, we are not done
    if (this.queue.length > 0) {
      this.classList.remove('visibility-hidden');
      return;
    }

    if (!(this.productsGrid.querySelectorAll(this.nameWrapperChildren).length < parseInt(this.perPage))) {
      this.classList.remove('visibility-hidden');
    }
  }

  async getNextPage() {
    try {
      const res = await fetch(this.paginationNextUrl);
      return await res.text();
    } catch (error) {
      console.error('Error fetching next page:', error);
      throw error;
    }
  }

  async handleLoadMoreProducts() {
    this.button.classList.add('hidden');
    this.spinner.classList.remove('hidden');

    // 1. Check if we have enough components in the queue
    if (this.queue.length >= this.targetPerPage) {
      const productsToRender = this.queue.splice(0, this.targetPerPage);
      this.renderFetchedProducts(productsToRender);
      this.checkUrl(this.paginationNextUrl); // Re-show button if valid
      this.spinner.classList.add('hidden');
      this.initWidgets(productsToRender);
      return;
    }

    // 2. Fetch loops until we meet the target count
    // We start with whatever is in the queue
    let gatheredProducts = [...this.queue];
    this.queue = []; // Clear queue as we moved items to gatheredProducts

    while (gatheredProducts.length < this.targetPerPage && this.paginationNextUrl) {
      const nextPage = await this.getNextPage();
      const parser = new DOMParser();
      const nextPageDoc = parser.parseFromString(nextPage, 'text/html');

      const responseProductGrid = nextPageDoc.querySelector(this.nameWrapper);
      const fetchedProductItems = Array.from(responseProductGrid.querySelectorAll(this.nameWrapperChildren));
      const loadMore = nextPageDoc.querySelector('loadmore-products');

      gatheredProducts = gatheredProducts.concat(fetchedProductItems);

      // Update next URL for next iteration
      if (loadMore && loadMore.dataset.nextUrl) {
        this.paginationNextUrl = loadMore.dataset.nextUrl;
      } else {
        this.paginationNextUrl = null; // No more pages
      }
    }

    // 3. Render and Re-Queue
    // Extract exactly what we need
    const productsToRender = gatheredProducts.splice(0, this.targetPerPage);
    // Put the rest back in queue
    this.queue = gatheredProducts;

    this.renderFetchedProducts(productsToRender);

    this.spinner.classList.add('hidden');
    // If we still have a next URL OR we have items in queue, show button
    this.checkUrl(this.paginationNextUrl || (this.queue.length > 0 ? 'queue-active' : null));

    this.initWidgets(productsToRender);
  }

  initWidgets(productItems) {
    if (!this.hasAttribute('data-show-rating')) return;
    
    // If no items provided, use all items in the grid (for initial page load)
    const items = productItems || Array.from(this.productsGrid.querySelectorAll(this.nameWrapperChildren));
    if (!items || items.length === 0) return;

    if (window.initYotpoRatings) {
      window.initYotpoRatings(items);
    }
  }

  renderFetchedProducts(productItems) {
    for (const product of [...productItems]) {
      this.productsGrid.appendChild(product);
    }

    // Check banners in last row after adding new products
    requestAnimationFrame(() => {
      gridBannerCheckBannersInLastRow(this.productsGrid);
      if (window.alignProductCardReviews) window.alignProductCardReviews();
    });
  }

  checkUrl(url) {
    if (url) {
      this.button.classList.remove('hidden');
      return;
    }
    this.classList.add('hidden');
  }
}

customElements.define('loadmore-products', LoadMoreProducts);

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const productGrid = document.querySelector('#product-grid');
  if (!productGrid) return;

  // Wait for layout to be ready
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      gridBannerCheckBannersInLastRow(productGrid);
    });
  });

  // Re-check on resize using debounce from global.js
  const debouncedCheck = debounce(() => gridBannerCheckBannersInLastRow(productGrid), 150);
  window.addEventListener('resize', debouncedCheck);

  // Also listen to media query changes for more accurate breakpoint detection
  _gridBannerMobileMediaQuery.addEventListener('change', () => gridBannerCheckBannersInLastRow(productGrid));
});
